import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";

// Checker background so transparent / white artwork stays visible.
const CHECKER_BG =
  "repeating-conic-gradient(#ececec 0% 25%, #fafafa 0% 50%) 50% / 16px 16px";

export interface ImagePreviewBoxProps {
  url?: string | null;
  alt?: string;
  height?: number;
  width?: number | string;
  // Overlaid in the top-left corner, e.g. a status chip.
  badge?: React.ReactNode;
  emptyIcon?: React.ReactNode;
  emptyText?: string;
}

// object-fit: contain is set on the sized <img> itself (not a wrapper) so the whole
// image always fits inside its box and can never be clipped.
const ImagePreviewBox: React.FC<ImagePreviewBoxProps> = ({
  url,
  alt = "preview",
  height = 180,
  width = "100%",
  badge,
  emptyIcon,
  emptyText = "No image",
}) => {
  const [errored, setErrored] = useState(false);

  // Reset the error state whenever the source changes (e.g. after a replace).
  useEffect(() => {
    setErrored(false);
  }, [url]);

  const showImage = Boolean(url) && !errored;

  const boxBaseSx = {
    width,
    height,
    borderRadius: 2,
    border: "1px solid #e3e6ea",
    boxSizing: "border-box" as const,
  };

  return (
    <Box sx={{ position: "relative", width }}>
      {showImage ? (
        <Box
          component="img"
          src={url as string}
          alt={alt}
          onError={() => setErrored(true)}
          sx={{
            ...boxBaseSx,
            display: "block",
            objectFit: "contain",
            p: 1,
            background: CHECKER_BG,
          }}
        />
      ) : (
        <Box
          sx={{
            ...boxBaseSx,
            background: "#f7f8fa",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5,
            color: "#aeb4bd",
            textAlign: "center",
            px: 2,
          }}
        >
          {emptyIcon ?? <ImageOutlinedIcon />}
          <Typography variant="caption">{emptyText}</Typography>
        </Box>
      )}

      {badge && <Box sx={{ position: "absolute", top: 8, left: 8 }}>{badge}</Box>}
    </Box>
  );
};

export default ImagePreviewBox;
